import { type ReactElement, useEffect, useState } from 'react';
import { type FoodAdCampaignLedgerView, type FoodAdKillSwitchView } from '@vello/api-client';
import { type FormatContext, formatMoney } from '@vello/format';
import { campaignSpend, killSwitchTone, moneyFromWire, type ConsoleTone, formatMarketInstant, marketZoneOfContext, scopeTimeZone } from '../../src/index';
import { useAdmin } from '../session';
import { IdRef } from '../components/code-ref';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, EmptyState, Field, Meter, PageHeader, Panel, TextInput, FormRow, SectionTitle, Stack, Row as LayoutRow, Text, Grid, Line, Box } from '@vello/web-theme';
import { DetailRow as Row } from '../components/detail-row';

const TONE_BADGE: Readonly<Record<ConsoleTone, 'neutral' | 'success' | 'danger' | 'warning'>> = { neutral: 'neutral', success: 'success', danger: 'danger', warning: 'warning' };

/**
 * **Governança de anúncios Food** (ads governance, M19/S199) — the platform operator surface over
 * `/v1/food-ads-governance/*`. It reads a merchant's sponsored-campaign charge ledgers (budget vs spent, cap vs
 * spent, opt-in / pause) and drives the global **kill switch** for a scope — engaging it blocks every further
 * charge instantly. Money is decoded from the wire (`{ amountMinor, currency }`) and rendered per locale via
 * `@vello/format`; server-authoritative (operator AAL2 + a scoped `config:*` grant).
 */
export function FoodAdsGovernanceScreen(): ReactElement {
  const { scope: operatorScope, markets } = useAdmin();
  const { t, lang } = useT();
  // D401: instants render in the operating market's zone (AGENTS: store UTC, render in the market zone).
  // When the scope names no single zone the render falls back to UTC and SAYS so (`formatMarketInstant`).
  const fmtCtx: FormatContext = { locale: lang, timeZone: scopeTimeZone(operatorScope.current, markets) ?? 'UTC', measurement: 'metric' };
  return (
    <>
      <PageHeader title={t('foodAds.title')} subtitle={t('foodAds.subtitle')} />
      <Stack gap={6}>
        <KillSwitchPanel fmtCtx={fmtCtx} />
        <CampaignLedgerPanel fmtCtx={fmtCtx} />
      </Stack>
    </>
  );
}

function KillSwitchPanel({ fmtCtx }: { fmtCtx: FormatContext }): ReactElement {
  const { runtime } = useAdmin();
  const { confirm, toast } = useConsole();
  const { t } = useT();
  const { fail } = useVocab();
  const [scope, setScope] = useState('');
  const [reason, setReason] = useState('');
  const [posture, setPosture] = useState<FoodAdKillSwitchView | undefined>(undefined);
  const [loadedScope, setLoadedScope] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [liveScope, setLiveScope] = useState<string | undefined>(undefined);
  const trimmed = scope.trim();

  // D854 (L3-025): the scope every charge answers to comes from the server, filled in and checked, so the operator never engages a scope no charge reads.
  useEffect(() => {
    let alive = true;
    void runtime.foodAdsGovernance.listKillSwitchScopes().then((res) => {
      if (!alive || !res.ok) return;
      const live = res.value.scopes.find((s) => s.scope === res.value.defaultScope);
      setLiveScope(res.value.defaultScope);
      setScope((current) => (current.length === 0 ? res.value.defaultScope : current));
      if (live !== undefined) { setPosture(live.killSwitch); setLoadedScope(live.scope); }
    });
    return () => { alive = false; };
  }, [runtime]);

  const consult = async (): Promise<void> => {
    if (busy || trimmed.length === 0) return;
    setBusy(true);
    const res = await runtime.foodAdsGovernance.getKillSwitch(trimmed);
    setBusy(false);
    if (res.ok) { setPosture(res.value.killSwitch); setLoadedScope(trimmed); }
    else toast({ message: t('food.err', { code: fail(res.error) }), tone: 'warning' });
  };

  const engage = async (): Promise<void> => {
    const r = await confirm({ title: t('foodAds.kill.confirm.title'), body: t('foodAds.kill.confirm.body', { scope: trimmed }), confirmLabel: t('foodAds.kill.engageConfirm'), cancelLabel: t('ui.cancel'), tone: 'danger' });
    if (!r.confirmed) return;
    setBusy(true);
    const res = await runtime.foodAdsGovernance.engageKillSwitch(trimmed, reason.trim().length > 0 ? reason.trim() : undefined);
    setBusy(false);
    if (res.ok) { setPosture(res.value.killSwitch); setReason(''); toast({ message: t('foodAds.kill.toast.engaged'), tone: 'success' }); }
    else toast({ message: t('food.err', { code: fail(res.error) }), tone: 'warning' });
  };

  const disengage = async (): Promise<void> => {
    const r = await confirm({ title: t('foodAds.kill.release.title'), body: t('foodAds.kill.release.body', { scope: trimmed }), confirmLabel: t('foodAds.kill.release'), cancelLabel: t('ui.cancel'), tone: 'primary' });
    if (!r.confirmed) return;
    setBusy(true);
    const res = await runtime.foodAdsGovernance.disengageKillSwitch(trimmed);
    setBusy(false);
    if (res.ok) { setPosture(res.value.killSwitch); toast({ message: t('foodAds.kill.toast.released'), tone: 'success' }); }
    else toast({ message: t('food.err', { code: fail(res.error) }), tone: 'warning' });
  };

  const showPosture = posture !== undefined && loadedScope === trimmed;

  return (
    <Panel><Stack gap={2}>
      <LayoutRow gap={2} align="center">
        <Text as="h2" role="title" className="vello-grow">{t('foodAds.kill.title')}</Text>
        {showPosture && <Badge tone={TONE_BADGE[killSwitchTone(posture)]}>{t(posture.engaged ? 'foodAds.kill.engaged' : 'foodAds.kill.released')}</Badge>}
      </LayoutRow>
      <Text as="p" role="caption" tone="secondary">{t('foodAds.kill.explain')}</Text>
      {liveScope !== undefined && <Text as="p" role="caption" tone="secondary">{t('foodAds.kill.live', { scope: liveScope })}</Text>}

      <FormRow
        action={
          <Button variant="secondary" onClick={() => void consult()} disabled={busy || trimmed.length === 0}>{t('foodAds.kill.consult')}</Button>
        }
      >
        <Field label={t('foodAds.field.scope')} htmlFor="fa-scope" hint={t('foodAds.field.scope.hint')}>
          <TextInput id="fa-scope" value={scope} onChange={(e) => setScope(e.target.value)} placeholder={t('foodAds.field.scope.ph')} />
        </Field>
      </FormRow>

      {showPosture && (
        <Stack gap={3}>
          <Row label={t('foodAds.kill.contractVersion')} value={String(posture.contractVersion)} />
          {posture.engagedAt !== undefined && <Row label={t('foodAds.kill.engagedAt')} value={formatMarketInstant(posture.engagedAt, marketZoneOfContext(fmtCtx), fmtCtx.locale)} />}
          {posture.reason !== undefined && <Row label={t('ui.reason')} value={posture.reason} />}
          {posture.engaged ? (
            <div><Button variant="primary" onClick={() => void disengage()} disabled={busy}>{t('foodAds.kill.release')}</Button></div>
          ) : (
            <Stack gap={2}>
              <Field label={t('ui.reason')} htmlFor="fa-reason">
                <TextInput id="fa-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('foodAds.kill.reason.ph')} />
              </Field>
              <div><Button variant="danger" onClick={() => void engage()} disabled={busy}>{t('foodAds.kill.engage')}</Button></div>
            </Stack>
          )}
        </Stack>
      )}
    </Stack></Panel>
  );
}

function CampaignLedgerPanel({ fmtCtx }: { fmtCtx: FormatContext }): ReactElement {
  const { runtime } = useAdmin();
  const { toast } = useConsole();
  const { t } = useT();
  const { fail } = useVocab();
  const [merchantId, setMerchantId] = useState('');
  const [campaigns, setCampaigns] = useState<readonly FoodAdCampaignLedgerView[] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const trimmed = merchantId.trim();

  const search = async (): Promise<void> => {
    if (busy || trimmed.length === 0) return;
    setBusy(true);
    const res = await runtime.foodAdsGovernance.listByMerchant(trimmed);
    setBusy(false);
    if (res.ok) setCampaigns(res.value.campaigns);
    else { setCampaigns(undefined); toast({ message: t('food.err', { code: fail(res.error) }), tone: 'warning' }); }
  };

  return (
    <Panel><Stack gap={2}>
      <SectionTitle>{t('foodAds.ledger.title')}</SectionTitle>
      <FormRow
        action={
          <Button variant="secondary" onClick={() => void search()} disabled={busy || trimmed.length === 0}>{t('foodAds.ledger.search')}</Button>
        }
      >
        <Field label={t('foodAds.field.merchantId')} htmlFor="fa-merchant">
          <TextInput id="fa-merchant" value={merchantId} onChange={(e) => setMerchantId(e.target.value)} placeholder={t('foodAds.field.merchantId.ph')} />
        </Field>
      </FormRow>

      {campaigns === undefined ? (
        <Text as="p" role="caption" tone="tertiary">{t('foodAds.ledger.prompt')}</Text>
      ) : campaigns.length === 0 ? (
        <EmptyState title={t('foodAds.ledger.empty')} />
      ) : (
        <Grid min="md" gap={4}>
          {campaigns.map((c) => <CampaignCard key={c.campaignId} ledger={c} fmtCtx={fmtCtx} />)}
        </Grid>
      )}
    </Stack></Panel>
  );
}

function CampaignCard({ ledger, fmtCtx }: { ledger: FoodAdCampaignLedgerView; fmtCtx: FormatContext }): ReactElement {
  const { t } = useT();
  const spend = campaignSpend(ledger);
  const line = (label: string, pct: number, spent: string, total: string): ReactElement => (
    <div>
      <Meter label={label} value={Math.max(0, Math.min(100, pct))} max={100} showMax={false} warnAt={0.8} dangerAt={1} warnLabel={t('foodAds.meter.near')} dangerLabel={t('foodAds.meter.reached')} formatValue={(n) => t('foodAds.pct', { pct: Math.round(n) })} />
      <Text as="div" role="caption" tone="secondary" mono><Line pad={2} caption>
        <span>{spent}</span>
        <span>{total}</span>
      </Line></Text>
    </div>
  );
  return (
    <Box gap={3} tone="subtle">
      <LayoutRow gap={2} align="baseline" justify="between">
        <IdRef value={ledger.campaignId} />
        <LayoutRow gap={1} align="stretch">
          <Badge tone={ledger.controls.optedIn ? 'success' : 'neutral'}>{t(ledger.controls.optedIn ? 'foodAds.optedIn' : 'foodAds.optedOut')}</Badge>
          {ledger.controls.paused && <Badge tone="warning">{t('foodAds.paused')}</Badge>}
        </LayoutRow>
      </LayoutRow>
      {line(t('foodAds.budget'), spend.budgetPct, formatMoney(moneyFromWire(ledger.budget.spent), fmtCtx), formatMoney(moneyFromWire(ledger.budget.budget), fmtCtx))}
      {line(t('foodAds.cap'), spend.capPct, formatMoney(moneyFromWire(ledger.controls.spent), fmtCtx), formatMoney(moneyFromWire(ledger.controls.spendCap), fmtCtx))}
      <Row label={t('foodAds.remaining')} value={formatMoney(spend.budgetRemaining, fmtCtx)} />
    </Box>
  );
}
